import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp,
  ShoppingBag,
  Package,
  Clock,
  CheckCircle2,
  AlertCircle,
  Truck,
  Users,
  Eye,
  ArrowUpRight,
  DollarSign,
  Calendar,
  Sparkles,
  Smartphone,
  Monitor,
  Plus,
  FileText,
  MessageCircle,
  Star,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Zap,
  ShieldCheck,
  Edit3,
  Layers,
  FolderTree,
  HeartPulse,
  Boxes,
  Settings,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useLanguage } from '../../context/LanguageContext';
import { formatPrice } from '../../utils/formatters';
import { getRealVisitorStats, fetchServerVisitorStats } from '../../utils/analyticsHelper';

export default function DashboardOverview({
  onNavigateTab,
  onOpenNewProduct,
  onOpenNewBlog,
  onSelectOrder
}) {
  const { lang } = useLanguage();
  const {
    products = [],
    orders = [],
    blogs = [],
    reviews = [],
    settings = {},
    categories = [],
    ailments = [],
    mizajList = [],
    pages = [],
    updateOrderStatus,
    addBlog
  } = useStore();

  const [visitorStats, setVisitorStats] = useState(() => getRealVisitorStats());
  const [quickDraftTitle, setQuickDraftTitle] = useState('');
  const [quickDraftContent, setQuickDraftContent] = useState('');
  const [quickDraftSaved, setQuickDraftSaved] = useState(false);

  // Refresh visitor stats (combining server data/analytics.json & local cache)
  useEffect(() => {
    let isMounted = true;
    const loadStats = async () => {
      try {
        const serverStats = await fetchServerVisitorStats();
        if (isMounted && serverStats) {
          setVisitorStats(serverStats);
          return;
        }
      } catch (e) {}
      if (isMounted) {
        setVisitorStats(getRealVisitorStats());
      }
    };

    loadStats();
    const interval = setInterval(loadStats, 15000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Order Metrics
  const totalRevenue = useMemo(() => {
    return orders.reduce((sum, o) => {
      if (o.status !== 'Cancelled') {
        return sum + (Number(o.totalAmount) || 0);
      }
      return sum;
    }, 0);
  }, [orders]);

  const pendingOrders = orders.filter(o => o.status === 'Pending');
  const processingOrders = orders.filter(o => o.status === 'Processing');
  const shippedOrders = orders.filter(o => o.status === 'Shipped');
  const deliveredOrders = orders.filter(o => o.status === 'Delivered');
  const cancelledOrders = orders.filter(o => o.status === 'Cancelled');

  const outOfStockProducts = products.filter(p => !p.inStock);
  const pendingReviews = reviews.filter(r => r.status === 'pending');

  // Recent 6 orders
  const recentOrders = useMemo(() => {
    return [...orders].slice(0, 6);
  }, [orders]);

  // Handle WordPress Quick Draft
  const handleSaveQuickDraft = (e) => {
    e?.preventDefault();
    if (!quickDraftTitle.trim()) return;

    if (addBlog) {
      addBlog({
        title: quickDraftTitle.trim(),
        titleUrdu: quickDraftTitle.trim(),
        content: `<p>${quickDraftContent.trim()}</p>`,
        contentHtml: `<p>${quickDraftContent.trim()}</p>`,
        category: 'herbs',
        categoryUrdu: 'جڑی بوٹیاں و نباتات',
        status: 'draft',
        publishedDate: new Date().toISOString().slice(0, 10),
        author: settings.hakimNameUrdu || 'حکیم مولانا سید عبدالوہاب شاہ شیرازی',
        excerpt: quickDraftContent.slice(0, 120),
        excerptUrdu: quickDraftContent.slice(0, 120)
      });
    }

    setQuickDraftTitle('');
    setQuickDraftContent('');
    setQuickDraftSaved(true);
    setTimeout(() => setQuickDraftSaved(false), 2500);
  };

  // WhatsApp quick chat with customer
  const handleOpenWhatsApp = (phone, customerName, orderId) => {
    const cleanPhone = (phone || '').replace(/[^0-9]/g, '').replace(/^0/, '92');
    const msg = `السلام علیکم محترم *${customerName}* صاحب! 🌿\nہم آپ کے ہمالین پنسار سٹور کے آرڈر نمبر *#${orderId}* کے سلسلے میں رابطہ کر رہے ہیں۔`;
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="space-y-7 animate-fade-in font-urdu" dir="rtl">
      
      {/* 1. TOP WELCOME & REAL-TIME SYSTEM BANNER */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-pansar-900 via-pansar-850 to-emerald-900 text-white p-6 sm:p-8 shadow-md border border-emerald-800/40">
        <div className="absolute top-0 left-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-400/30">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
              <span>سٹور آن لائن ہے | ایڈمن کنٹرول پینل</span>
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-white">
              خوش آمدید! {settings.storeNameUrdu || 'ہمالین پنسار سٹور'}
            </h1>
            <p className="text-xs sm:text-sm text-emerald-100 max-w-2xl leading-relaxed">
              یہاں سے آپ آرڈرز کی لائیو صورتحال، حقیقی وزٹرز کی تعداد، پروڈکٹس سٹاک اور ویب سائٹ کی سرگرمیوں کا مکمل جائزہ لے سکتے ہیں۔
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 shrink-0">
            <button
              type="button"
              onClick={() => onNavigateTab && onNavigateTab('orders')}
              className="px-4 py-2.5 rounded-2xl bg-amber-500 hover:bg-amber-400 text-gray-950 font-black text-xs shadow-md transition cursor-pointer flex items-center gap-1.5"
            >
              <ShoppingBag className="w-4 h-4" />
              <span>تمام آرڈرز دیکھیں</span>
            </button>

            {onOpenNewProduct && (
              <button
                type="button"
                onClick={onOpenNewProduct}
                className="px-4 py-2.5 rounded-2xl bg-white/15 hover:bg-white/25 text-white font-bold text-xs border border-white/20 transition cursor-pointer flex items-center gap-1.5 backdrop-blur-xs"
              >
                <Plus className="w-4 h-4" />
                <span>+ نئی پروڈکٹ</span>
              </button>
            )}

            {onOpenNewBlog && (
              <button
                type="button"
                onClick={onOpenNewBlog}
                className="px-4 py-2.5 rounded-2xl bg-white/15 hover:bg-white/25 text-white font-bold text-xs border border-white/20 transition cursor-pointer flex items-center gap-1.5 backdrop-blur-xs"
              >
                <FileText className="w-4 h-4" />
                <span>+ نیا مضمون</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => onNavigateTab && onNavigateTab('settings')}
              className="px-4 py-2.5 rounded-2xl bg-white/15 hover:bg-white/25 text-white font-bold text-xs border border-white/20 transition cursor-pointer flex items-center gap-1.5 backdrop-blur-xs"
              title="براؤزر ٹیب آئیکون اور سٹور ترتیبات"
            >
              <Settings className="w-4 h-4 text-emerald-300" />
              <span>براؤزر آئیکون و سیٹنگز</span>
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 2. LIVE STORE DATA SUMMARY (سٹور ڈیٹا کی موجودہ صورتحال) */}
      {/* ========================================================================= */}
      <div className="bg-white rounded-3xl p-5 sm:p-6 border border-gray-200/80 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-3.5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-emerald-50 text-pansar-800 flex items-center justify-center border border-emerald-100/80 shadow-2xs">
              <Boxes className="w-5 h-5 text-pansar-700" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black text-gray-900 flex items-center gap-2">
                <span>سٹور ڈیٹا کی موجودہ صورتحال</span>
                <span className="text-xs font-sans font-semibold text-gray-400 font-normal">(Live Store Summary)</span>
              </h2>
              <p className="text-[11px] text-gray-500">
                سٹور کے تمام فعال اجزاء، ادویات، کیٹیگریز، طبی تشخیص اور ڈیٹا کا لائیو ریکارڈ
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 self-start sm:self-auto text-[11px] text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200/70 font-sans">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>ریئل ٹائم ہم وقت سازی (Live Synchronized)</span>
          </div>
        </div>

        {/* 6 Grid Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          
          {/* Card 1: Products */}
          <div
            onClick={() => onNavigateTab && onNavigateTab('products')}
            className="p-4 rounded-2xl bg-emerald-50/80 hover:bg-emerald-100/90 border border-emerald-200/70 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm cursor-pointer group flex flex-col justify-between"
            title="پروڈکٹس مینیجر پر جائیں"
          >
            <div className="flex items-center justify-center mb-1.5">
              <span className="w-8 h-8 rounded-xl bg-white text-emerald-700 flex items-center justify-center shadow-2xs group-hover:scale-110 transition-transform">
                <Package className="w-4 h-4" />
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-emerald-950 font-sans">
              {products.length}
            </div>
            <div className="text-xs text-emerald-900 font-bold mt-1">
              کل ادویات / پروڈکٹس
            </div>
            <div className="text-[10px] text-emerald-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-1">
              معائنہ کریں ←
            </div>
          </div>

          {/* Card 2: Categories */}
          <div
            onClick={() => onNavigateTab && onNavigateTab('categories')}
            className="p-4 rounded-2xl bg-amber-50/80 hover:bg-amber-100/90 border border-amber-200/70 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm cursor-pointer group flex flex-col justify-between"
            title="کیٹیگریز مینیجر پر جائیں"
          >
            <div className="flex items-center justify-center mb-1.5">
              <span className="w-8 h-8 rounded-xl bg-white text-amber-700 flex items-center justify-center shadow-2xs group-hover:scale-110 transition-transform">
                <FolderTree className="w-4 h-4" />
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-amber-950 font-sans">
              {categories.length}
            </div>
            <div className="text-xs text-amber-900 font-bold mt-1">
              کل کیٹیگریز
            </div>
            <div className="text-[10px] text-amber-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-1">
              معائنہ کریں ←
            </div>
          </div>

          {/* Card 3: Ailments */}
          <div
            onClick={() => onNavigateTab && onNavigateTab('categories')}
            className="p-4 rounded-2xl bg-blue-50/80 hover:bg-blue-100/90 border border-blue-200/70 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm cursor-pointer group flex flex-col justify-between"
            title="امراض فلٹرز پر جائیں"
          >
            <div className="flex items-center justify-center mb-1.5">
              <span className="w-8 h-8 rounded-xl bg-white text-blue-700 flex items-center justify-center shadow-2xs group-hover:scale-110 transition-transform">
                <HeartPulse className="w-4 h-4" />
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-blue-950 font-sans">
              {ailments.length}
            </div>
            <div className="text-xs text-blue-900 font-bold mt-1">
              امراض فلٹرز
            </div>
            <div className="text-[10px] text-blue-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-1">
              معائنہ کریں ←
            </div>
          </div>

          {/* Card 4: Mizaj Profiles */}
          <div
            onClick={() => onNavigateTab && onNavigateTab('categories')}
            className="p-4 rounded-2xl bg-purple-50/80 hover:bg-purple-100/90 border border-purple-200/70 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm cursor-pointer group flex flex-col justify-between"
            title="مزاج پروفائلز دیکھیں"
          >
            <div className="flex items-center justify-center mb-1.5">
              <span className="w-8 h-8 rounded-xl bg-white text-purple-700 flex items-center justify-center shadow-2xs group-hover:scale-110 transition-transform">
                <Layers className="w-4 h-4" />
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-purple-950 font-sans">
              {mizajList.length}
            </div>
            <div className="text-xs text-purple-900 font-bold mt-1">
              مزاج پروفائلز
            </div>
            <div className="text-[10px] text-purple-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-1">
              معائنہ کریں ←
            </div>
          </div>

          {/* Card 5: Blogs */}
          <div
            onClick={() => onNavigateTab && onNavigateTab('blogs')}
            className="p-4 rounded-2xl bg-rose-50/80 hover:bg-rose-100/90 border border-rose-200/70 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm cursor-pointer group flex flex-col justify-between"
            title="بلاگ مقالات مینیجر پر جائیں"
          >
            <div className="flex items-center justify-center mb-1.5">
              <span className="w-8 h-8 rounded-xl bg-white text-rose-700 flex items-center justify-center shadow-2xs group-hover:scale-110 transition-transform">
                <FileText className="w-4 h-4" />
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-rose-950 font-sans">
              {blogs.length}
            </div>
            <div className="text-xs text-rose-900 font-bold mt-1">
              بلاگ مقالات
            </div>
            <div className="text-[10px] text-rose-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-1">
              معائنہ کریں ←
            </div>
          </div>

          {/* Card 6: Orders */}
          <div
            onClick={() => onNavigateTab && onNavigateTab('orders')}
            className="p-4 rounded-2xl bg-teal-50/80 hover:bg-teal-100/90 border border-teal-200/70 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm cursor-pointer group flex flex-col justify-between"
            title="موصولہ آرڈرز دیکھیں"
          >
            <div className="flex items-center justify-center mb-1.5">
              <span className="w-8 h-8 rounded-xl bg-white text-teal-700 flex items-center justify-center shadow-2xs group-hover:scale-110 transition-transform">
                <ShoppingBag className="w-4 h-4" />
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-black text-teal-950 font-sans">
              {orders.length}
            </div>
            <div className="text-xs text-teal-900 font-bold mt-1">
              موصولہ آرڈرز
            </div>
            <div className="text-[10px] text-teal-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity mt-1">
              معائنہ کریں ←
            </div>
          </div>

        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3. WOOCOMMERCE ORDER & SALES METRICS CARDS */}
      {/* ========================================================================= */}
      <div>
        <div className="flex items-center justify-between mb-3.5">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-pansar-700" />
            <span>آرڈرز اور سیلز کا جائزہ (WooCommerce Order Metrics)</span>
          </h2>
          <span className="text-xs text-gray-500 font-sans">
            کل ریکارڈ شدہ آرڈرز: <strong>{orders.length}</strong>
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          
          {/* Total Revenue Card */}
          <div className="bg-white rounded-3xl p-5 border border-gray-200/80 shadow-xs hover:shadow-md transition">
            <div className="flex items-center justify-between mb-2">
              <span className="p-2 rounded-2xl bg-emerald-50 text-emerald-700">
                <DollarSign className="w-5 h-5" />
              </span>
              <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                کل فروخت
              </span>
            </div>
            <div className="text-xs font-bold text-gray-500">مجموعی سیلز ریونیو</div>
            <div className="text-xl sm:text-2xl font-black text-gray-900 font-sans mt-0.5">
              {formatPrice(totalRevenue, lang)}
            </div>
          </div>

          {/* Pending Orders Card */}
          <div 
            onClick={() => onNavigateTab && onNavigateTab('orders')}
            className="bg-amber-50/70 hover:bg-amber-50 rounded-3xl p-5 border border-amber-200/80 shadow-xs hover:shadow-md transition cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="p-2 rounded-2xl bg-amber-100 text-amber-800">
                <Clock className="w-5 h-5" />
              </span>
              <span className="text-[11px] font-bold text-amber-900 bg-amber-200/70 px-2 py-0.5 rounded-full">
                نئے آرڈرز
              </span>
            </div>
            <div className="text-xs font-bold text-amber-900">پینڈنگ آرڈرز (Pending)</div>
            <div className="text-2xl sm:text-3xl font-black text-amber-950 font-sans mt-0.5 flex items-center justify-between">
              <span>{pendingOrders.length}</span>
              <span className="text-xs text-amber-800 group-hover:translate-x-[-4px] transition-transform">معائنہ کریں ←</span>
            </div>
          </div>

          {/* Processing / In Progress */}
          <div className="bg-blue-50/70 rounded-3xl p-5 border border-blue-200/80 shadow-xs hover:shadow-md transition">
            <div className="flex items-center justify-between mb-2">
              <span className="p-2 rounded-2xl bg-blue-100 text-blue-800">
                <RefreshCw className="w-5 h-5" />
              </span>
              <span className="text-[11px] font-bold text-blue-800 bg-blue-200/70 px-2 py-0.5 rounded-full">
                تیاری میں
              </span>
            </div>
            <div className="text-xs font-bold text-blue-900">زیرِ عمل آرڈرز (Processing)</div>
            <div className="text-2xl sm:text-3xl font-black text-blue-950 font-sans mt-0.5">
              {processingOrders.length}
            </div>
          </div>

          {/* Shipped & Dispatched */}
          <div className="bg-purple-50/70 rounded-3xl p-5 border border-purple-200/80 shadow-xs hover:shadow-md transition">
            <div className="flex items-center justify-between mb-2">
              <span className="p-2 rounded-2xl bg-purple-100 text-purple-800">
                <Truck className="w-5 h-5" />
              </span>
              <span className="text-[11px] font-bold text-purple-800 bg-purple-200/70 px-2 py-0.5 rounded-full">
                راستے میں
              </span>
            </div>
            <div className="text-xs font-bold text-purple-900">روانہ شدہ پارسلز (Shipped)</div>
            <div className="text-2xl sm:text-3xl font-black text-purple-950 font-sans mt-0.5">
              {shippedOrders.length}
            </div>
          </div>

        </div>

        {/* Second Row Badges */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <div className="bg-emerald-50/60 p-3.5 rounded-2xl border border-emerald-100 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-bold text-emerald-800 block">مکمل شدہ (Delivered)</span>
              <span className="text-lg font-black text-emerald-950 font-sans">{deliveredOrders.length}</span>
            </div>
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>

          <div className="bg-rose-50/60 p-3.5 rounded-2xl border border-rose-100 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-bold text-rose-800 block">منسوخ شدہ (Cancelled)</span>
              <span className="text-lg font-black text-rose-950 font-sans">{cancelledOrders.length}</span>
            </div>
            <AlertCircle className="w-5 h-5 text-rose-600" />
          </div>

          <div 
            onClick={() => onNavigateTab && onNavigateTab('products')}
            className={`p-3.5 rounded-2xl border flex items-center justify-between cursor-pointer transition ${
              outOfStockProducts.length > 0 
                ? 'bg-rose-100/70 border-rose-300 text-rose-950 hover:bg-rose-100' 
                : 'bg-white border-gray-200 text-gray-700'
            }`}
          >
            <div>
              <span className="text-[11px] font-bold block">آؤٹ آف سٹاک ادویات</span>
              <span className="text-lg font-black font-sans">{outOfStockProducts.length}</span>
            </div>
            <Package className="w-5 h-5 text-gray-500" />
          </div>

          <div 
            onClick={() => onNavigateTab && onNavigateTab('reviews')}
            className="bg-amber-50/60 p-3.5 rounded-2xl border border-amber-100 flex items-center justify-between cursor-pointer hover:bg-amber-100/70 transition"
          >
            <div>
              <span className="text-[11px] font-bold text-amber-800 block">نئے کسٹمر ریویوز</span>
              <span className="text-lg font-black text-amber-950 font-sans">{pendingReviews.length}</span>
            </div>
            <Star className="w-5 h-5 text-amber-500 fill-amber-400" />
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3. REAL VISITOR ANALYTICS SECTION (حقیقی وزٹرز کی شماریات) */}
      {/* ========================================================================= */}
      <div className="bg-white rounded-3xl p-5 sm:p-7 border border-gray-200/80 shadow-xs space-y-5">
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-4">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-700" />
              <span>ویب سائٹ وزٹرز ٹریفک (Real Visitor Analytics)</span>
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              یہ اعداد و شمار ویب سائٹ پر آنے والے حقیقی وزیٹرز کے سیشنز اور پیج ویوز پر مبنی ہیں۔
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold border border-emerald-200">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>لائیو ٹریکر فعال</span>
            </span>
          </div>
        </div>

        {/* 4 Cards: Today, This Week, This Month, Total */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          
          <div className="bg-emerald-50/70 p-4 rounded-2xl border border-emerald-200/80 shadow-2xs">
            <span className="text-xs font-bold text-emerald-800 block">آج کے حقیقی وزٹرز</span>
            <div className="text-2xl sm:text-3xl font-black text-emerald-950 font-sans mt-1">
              {visitorStats.today}
            </div>
            <span className="text-[10px] text-emerald-700 mt-0.5 block">24 گھنٹے میں منفرد وزٹ</span>
          </div>

          <div className="bg-blue-50/70 p-4 rounded-2xl border border-blue-200/80 shadow-2xs">
            <span className="text-xs font-bold text-blue-800 block">اس ہفتے کے وزٹرز</span>
            <div className="text-2xl sm:text-3xl font-black text-blue-950 font-sans mt-1">
              {visitorStats.thisWeek}
            </div>
            <span className="text-[10px] text-blue-700 mt-0.5 block">گزشتہ 7 دنوں میں</span>
          </div>

          <div className="bg-purple-50/70 p-4 rounded-2xl border border-purple-200/80 shadow-2xs">
            <span className="text-xs font-bold text-purple-800 block">اس مہینے کے وزٹرز</span>
            <div className="text-2xl sm:text-3xl font-black text-purple-950 font-sans mt-1">
              {visitorStats.thisMonth}
            </div>
            <span className="text-[10px] text-purple-700 mt-0.5 block">گزشتہ 30 دنوں میں</span>
          </div>

          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 shadow-2xs">
            <span className="text-xs font-bold text-slate-700 block">کل مجموعی وزٹرز</span>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 font-sans mt-1">
              {visitorStats.total}
            </div>
            <span className="text-[10px] text-slate-500 mt-0.5 block">آل ٹائم مجموعی دورے</span>
          </div>

        </div>

        {/* Traffic Trend Visual Bars & Device Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 pt-2">
          
          {/* 7-Day Trend Bars */}
          <div className="lg:col-span-8 p-4 rounded-2xl bg-gray-50 border border-gray-200/80 space-y-3">
            <div className="flex items-center justify-between text-xs font-bold text-gray-700">
              <span>گزشتہ 7 دنوں کا ٹریفک گراف (7-Day Traffic Trend)</span>
              <span className="text-gray-400 font-sans text-[11px]">روزانہ وزٹس</span>
            </div>

            <div className="flex items-end justify-between gap-2 h-36 pt-4 px-2">
              {visitorStats.dailyTrend && visitorStats.dailyTrend.length > 0 ? (
                visitorStats.dailyTrend.map((item, idx) => {
                  const maxCount = Math.max(1, ...visitorStats.dailyTrend.map(d => d.count));
                  const heightPercent = Math.max(12, Math.round((item.count / maxCount) * 100));

                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end group">
                      <span className="text-[10px] font-sans font-bold text-gray-600 group-hover:text-emerald-700">
                        {item.count}
                      </span>
                      <div className="w-full max-w-[36px] bg-gray-200 rounded-t-xl overflow-hidden h-full flex items-end">
                        <div
                          className="w-full bg-emerald-600 group-hover:bg-emerald-700 rounded-t-xl transition-all duration-500"
                          style={{ height: `${heightPercent}%` }}
                        />
                      </div>
                      <span className="text-[10px] font-sans text-gray-500">
                        {item.label}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="w-full text-center text-gray-400 text-xs py-8">
                  وزٹرز کا ڈیٹا جمع ہو رہا ہے...
                </div>
              )}
            </div>
          </div>

          {/* Device Breakdown & Real Referrer */}
          <div className="lg:col-span-4 p-4 rounded-2xl bg-gray-50 border border-gray-200/80 space-y-4 flex flex-col justify-between">
            <div className="text-xs font-bold text-gray-700">
              ڈیوائس استعمال کی تقسیم (Device Breakdown)
            </div>

            <div className="space-y-3">
              {/* Mobile */}
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="flex items-center gap-1.5 text-gray-700">
                    <Smartphone className="w-3.5 h-3.5 text-emerald-700" />
                    <span>موبائل وزٹرز (Mobile)</span>
                  </span>
                  <span className="font-bold font-sans">{visitorStats.mobilePercent}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full bg-emerald-600 rounded-full"
                    style={{ width: `${visitorStats.mobilePercent}%` }}
                  />
                </div>
              </div>

              {/* Desktop */}
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="flex items-center gap-1.5 text-gray-700">
                    <Monitor className="w-3.5 h-3.5 text-blue-700" />
                    <span>کمپیوٹر / لیپ ٹاپ (Desktop)</span>
                  </span>
                  <span className="font-bold font-sans">{visitorStats.desktopPercent}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full bg-blue-600 rounded-full"
                    style={{ width: `${visitorStats.desktopPercent}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-200/80 text-[11px] text-gray-500 leading-relaxed">
              💡 موبائل آپٹمائزیشن بہترین ہے جس سے واٹس ایپ آرڈرز کا تناسب تیزی سے بڑھتا ہے۔
            </div>
          </div>

        </div>

      </div>

      {/* ========================================================================= */}
      {/* 4. RECENT ORDERS TABLE & WORDPRESS QUICK DRAFT */}
      {/* ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left 8 Cols: Recent Customer Orders */}
        <div className="lg:col-span-8 bg-white rounded-3xl p-5 sm:p-6 border border-gray-200/80 shadow-xs space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h3 className="font-bold text-base text-gray-900 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-emerald-700" />
              <span>تازہ ترین موصولہ آرڈرز (Recent Orders)</span>
            </h3>
            <button
              type="button"
              onClick={() => onNavigateTab && onNavigateTab('orders')}
              className="text-xs font-bold text-pansar-800 hover:underline flex items-center gap-1 cursor-pointer"
            >
              <span>تمام آرڈرز لسٹ</span>
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
          </div>

          {recentOrders.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-xs">
              فی الحال کوئی آرڈر موجود نہیں ہے۔
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-right text-xs">
                <thead className="text-gray-500 bg-gray-50/80 border-b border-gray-100">
                  <tr>
                    <th className="p-2.5 font-bold">آرڈر ID</th>
                    <th className="p-2.5 font-bold">گاہک کا نام</th>
                    <th className="p-2.5 font-bold">شہر / پتہ</th>
                    <th className="p-2.5 font-bold">کل رقم</th>
                    <th className="p-2.5 font-bold">سٹیٹس</th>
                    <th className="p-2.5 font-bold text-center">واٹس ایپ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-800">
                  {recentOrders.map((o) => (
                    <tr key={o.id} className="hover:bg-gray-50/50 transition">
                      <td className="p-2.5 font-mono font-bold text-emerald-800">
                        #{o.id}
                      </td>
                      <td className="p-2.5 font-bold">
                        {o.customerName || 'نامعلوم'}
                      </td>
                      <td className="p-2.5 text-gray-600 truncate max-w-[120px]">
                        {o.city || o.address || '—'}
                      </td>
                      <td className="p-2.5 font-sans font-bold text-emerald-900">
                        Rs. {o.totalAmount}
                      </td>
                      <td className="p-2.5">
                        <select
                          value={o.status || 'Pending'}
                          onChange={(e) => {
                            if (updateOrderStatus) {
                              updateOrderStatus(o.id, e.target.value);
                            }
                          }}
                          className={`px-2 py-1 rounded-xl text-[11px] font-bold cursor-pointer border ${
                            o.status === 'Delivered'
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                              : o.status === 'Shipped'
                                ? 'bg-purple-50 text-purple-800 border-purple-200'
                                : o.status === 'Processing'
                                  ? 'bg-blue-50 text-blue-800 border-blue-200'
                                  : o.status === 'Cancelled'
                                    ? 'bg-rose-50 text-rose-800 border-rose-200'
                                    : 'bg-amber-50 text-amber-800 border-amber-200'
                          }`}
                        >
                          <option value="Pending">پینڈنگ (Pending)</option>
                          <option value="Processing">زیرِ عمل (Processing)</option>
                          <option value="Shipped">روانہ شدہ (Shipped)</option>
                          <option value="Delivered">ڈیلیور شدہ (Delivered)</option>
                          <option value="Cancelled">منسوخ (Cancelled)</option>
                        </select>
                      </td>
                      <td className="p-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => handleOpenWhatsApp(o.phone, o.customerName, o.id)}
                          className="p-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-900 transition cursor-pointer"
                          title="کسٹمر کو واٹس ایپ پیغام بھیجیں"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right 4 Cols: WordPress Quick Draft Widget */}
        <div className="lg:col-span-4 bg-white rounded-3xl p-5 sm:p-6 border border-gray-200/80 shadow-xs space-y-4">
          <div className="border-b border-gray-100 pb-3 flex items-center justify-between">
            <h3 className="font-bold text-base text-gray-900 flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-pansar-700" />
              <span>کوئیک ڈرافٹ (WordPress Quick Draft)</span>
            </h3>
            <span className="text-[10px] text-gray-400">فوری تحریر</span>
          </div>

          <form onSubmit={handleSaveQuickDraft} className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                مضمون یا نکتہ کا عنوان:
              </label>
              <input
                type="text"
                value={quickDraftTitle}
                onChange={(e) => setQuickDraftTitle(e.target.value)}
                placeholder="مثلاً: موسمِ سرما میں شہد کے فوائد..."
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-emerald-600"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                آپ کے ذہن میں کیا نکتہ ہے؟ (مختصر نوٹ):
              </label>
              <textarea
                rows={4}
                value={quickDraftContent}
                onChange={(e) => setQuickDraftContent(e.target.value)}
                placeholder="یہاں اہم طبی نکتہ یا مسودہ درج کریں، یہ خودکار طور پر ڈرافٹ کے طور پر محفوظ ہو جائے گا..."
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-emerald-600 leading-relaxed"
              />
            </div>

            {quickDraftSaved && (
              <div className="p-2 rounded-xl bg-emerald-100 text-emerald-800 text-xs font-bold flex items-center gap-1 animate-pulse">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>مسودہ بطور ڈرافٹ محفوظ ہو گیا!</span>
              </div>
            )}

            <button
              type="submit"
              className="w-full py-2.5 rounded-xl bg-pansar-700 hover:bg-pansar-800 text-white font-bold text-xs shadow-xs transition cursor-pointer"
            >
              بطور ڈرافٹ محفوظ کریں (Save Draft)
            </button>
          </form>
        </div>

      </div>

    </div>
  );
}
